import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  Flame,
  MessageCircle,
  Target,
} from "lucide-react";

import useSEO from "../lib/useSEO";
import { BRAND, buildWhatsAppLink } from "../lib/constants";

const VALUES = [
  {
    icon: Compass,
    title: "Co-Creator, Bukan Vendor",
    desc: "Kami duduk bareng kamu. Brief tim kamu jadi project tim kami.",
  },
  {
    icon: Flame,
    title: "Eksekusi Konsisten",
    desc: "Kualitas sublimasi dijaga konsisten dari pcs pertama sampai pesanan ke-1000.",
  },
  {
    icon: Target,
    title: "Detail Itu Segalanya",
    desc: "Mulai dari pemilihan kain, ketajaman warna, sampai jahitan akhir — semua kami cek manual.",
  },
];

const ADVANTAGES = [
  "Workshop sendiri di Wonosobo (bukan reseller)",
  "Bahan kain dipilih langsung dari supplier terpercaya",
  "Tim desain in-house full-time",
  "Quality check 3 lapis sebelum dikirim",
  "Ongkir hemat dari kantor pusat ekspedisi Jateng",
  "Garansi cetak ulang jika ada cacat produksi",
];

export default function AboutPage() {
  useSEO({
    title:
      "Tentang Kami — Workshop Sublimasi Wonosobo | Ace Sublimasi Nusantara",
    description:
      "Cerita di balik Ace Sublimasi Nusantara: workshop sublimasi premium dari Wonosobo yang melayani jersey futsal, jaket anime, haori, racing apparel, dan streetwear seluruh Indonesia.",
    keywords:
      "tentang ace sublimasi nusantara, workshop sublimasi wonosobo, produsen jersey futsal indonesia",
    canonical: window.location.origin + "/tentang",
  });

  return (
    <div data-testid="about-page" className="border-b border-[#1f1f1f]">
      {/* Hero */}
      <section className="border-b border-[#1f1f1f] py-20 md:py-28 bg-[#070707]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
              / Tentang {BRAND.name}
            </p>
            <h1 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] text-white">
              Bukan Sekadar <br />
              <span className="text-[#CCFF00]">Bisnis Apparel.</span> <br />
              <span className="text-outline">Ini Misi Kreatif.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#C2C2C2]">
              Ace Sublimasi Nusantara lahir dari Wonosobo dengan satu misi: bantu tim, komunitas,
              dan brand di Indonesia tampil seperti versi terbaik diri mereka — lewat apparel custom
              berkualitas yang nggak bikin kompromi soal harga, kecepatan, atau hasil.
            </p>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            <img
              src="https://images.unsplash.com/photo-1768084356884-22bb77e76931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI4MHww&ixlib=rb-4.1.0&q=85"
              alt="workshop"
              className="aspect-[3/4] object-cover border border-[#262626]"
            />
            <img
              src="https://images.unsplash.com/photo-1767036841778-294c81791933?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxyYWNpbmclMjBqYWNrZXQlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI5Nnww&ixlib=rb-4.1.0&q=85"
              alt="hasil"
              className="aspect-[3/4] object-cover border border-[#262626] mt-8"
            />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-24 border-b border-[#1f1f1f]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
            / Cerita Singkat
          </p>
          <h2 className="font-display text-3xl sm:text-5xl uppercase leading-tight text-white mb-8">
            Mulai dari workshop kecil, sekarang melayani <span className="text-[#CCFF00]">seluruh Indonesia.</span>
          </h2>
          <div className="prose-streetwear">
            <p>
              Berawal dari obrolan tim futsal kampus yang capek dengan vendor lambat dan kualitas seadanya,
              Ace Sublimasi Nusantara dibangun dengan satu prinsip sederhana: <strong>jersey custom harusnya
              bisa cepat, premium, dan terjangkau di waktu yang sama.</strong>
            </p>
            <p>
              Sejak itu, kami berkembang dari satu mesin sublimasi di garasi rumah jadi workshop full-fledged
              di Perumahan Bumi Dieng Indah, Wonosobo. Sekarang kami melayani tim futsal, komunitas anime, klub
              motor, brand streetwear independen, sampai sekolah dan kampus dari Aceh sampai Papua.
            </p>
            <p>
              Yang nggak berubah dari hari pertama: <strong>tim desain duduk bareng kamu, bukan di balik tembok</strong>.
              Semua brief kami eksekusi langsung — bukan dilempar ke jasa lain.
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="py-20 md:py-24 border-b border-[#1f1f1f] bg-[#070707]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
              / Visi & Nilai
            </p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none text-white">
              Apparel Itu Identitas. <br />
              <span className="text-[#CCFF00]">Kami Bikin Identitas Itu Berbicara.</span>
            </h2>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-1 gap-4">
            {VALUES.map((v, i) => (
              <div
                key={v.title}
                data-testid={`about-value-${i}`}
                className="border border-[#262626] p-6 flex gap-5 items-start hover:border-[#CCFF00] transition-colors"
              >
                <div className="w-12 h-12 border border-[#262626] inline-flex items-center justify-center text-[#CCFF00] shrink-0">
                  <v.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display text-xl uppercase text-white tracking-tight">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#A3A3A3]">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Production advantages */}
      <section className="py-20 md:py-24 border-b border-[#1f1f1f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-6 mb-10">
            <div className="md:col-span-7">
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
                / Keunggulan Produksi
              </p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none text-white">
                6 Hal Yang Bikin <br />
                <span className="text-outline">Kami Beda di Pasar.</span>
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-0 border border-[#262626]">
            {ADVANTAGES.map((a, i) => (
              <div
                key={a}
                data-testid={`about-advantage-${i}`}
                className="p-5 md:p-6 flex items-start gap-3 border-b border-[#262626] sm:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0 [&:not(:nth-last-child(-n+1))]:border-b sm:[&:nth-child(odd)]:border-r"
              >
                <CheckCircle2 className="w-5 h-5 text-[#CCFF00] shrink-0 mt-0.5" />
                <span className="text-base text-white">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-6xl uppercase leading-none text-white">
            Mau Jadi <span className="text-[#CCFF00]">Customer Berikutnya?</span>
          </h2>
          <p className="mt-5 text-base text-[#C2C2C2]">
            Setiap project, kami eksekusi seperti project pertama kami.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="about-cta-wa"
              className="btn-primary"
            >
              <MessageCircle className="w-5 h-5" />
              Chat Admin
            </a>
            <Link to="/shop" className="btn-ghost" data-testid="about-cta-shop">
              Lihat Katalog <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
